import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import { FieldDebateView } from "@/components/analyze/FieldDebateView";

export const Route = createFileRoute("/shared/$token")({
  component: SharedDebatePage,
});

interface SharedDebateData {
  concept_name: string;
  debate_map: {
    generated_at: string;
    camps: Array<{
      id: string;
      label: string;
      description: string;
      position_summary: string;
      paper_count: number;
    }>;
    tensions: Array<{
      camp_a: string;
      camp_b: string;
      description: string;
      tension_type: string;
    }>;
    bridge_papers: Array<{ camps: string[]; role: string }>;
    blind_spots: Array<{
      camp_id: string;
      gap: string;
      suggested_direction: string;
    }>;
    cluster_blind_spots?: Record<string, string[]>;
    scope?: {
      min_score: number;
      max_papers: number;
      out_of_scope_count: number;
      fallback_all: boolean;
    };
    partial?: boolean;
    partial_error?: string | null;
  };
}

function SharedDebatePage() {
  const { token } = Route.useParams();
  const [data, setData] = useState<SharedDebateData | null>(null);
  const [error, setError] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const apiUrl = import.meta.env.VITE_API_URL ?? "http://localhost:8000";
    fetch(`${apiUrl}/api/shared/${token}`)
      .then((res) => {
        if (!res.ok) throw new Error("Not found");
        return res.json();
      })
      .then(setData)
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  }, [token]);

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <Loader2 className="size-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-4 bg-background text-foreground">
        <p className="text-lg font-medium">This link is no longer available</p>
        <p className="text-sm text-muted-foreground">
          The author may have revoked sharing, or the link may be invalid.
        </p>
        <a
          href="/"
          className="mt-4 rounded-lg bg-primary px-6 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 transition-colors"
        >
          Go to PaperChase
        </a>
      </div>
    );
  }

  // Adapt shared response to DebateMap shape for FieldDebateView
  const debateMap = {
    generated_at: data.debate_map.generated_at,
    camps: data.debate_map.camps.map((c) => ({
      ...c,
      exemplar_paper_ids: [] as string[],
      paper_ids: [] as string[],
      your_claims: [] as Array<{ claim_id: string; claim_text: string; alignment: string }>,
    })),
    tensions: data.debate_map.tensions,
    bridge_papers: data.debate_map.bridge_papers.map((bp) => ({
      ...bp,
      paper_id: "",
    })),
    blind_spots: data.debate_map.blind_spots,
    cluster_blind_spots: data.debate_map.cluster_blind_spots ?? undefined,
    // Scope disclosure must survive the share surface: external viewers can't
    // inspect the corpus, so the Out-of-scope bucket is their only signal
    // that low-relevance papers were deliberately not assessed (review F4).
    scope: data.debate_map.scope,
    // Partial (batch-pending) state must survive too — otherwise a head-only
    // skeleton map reads as complete ("Generated …") on public links (review
    // MED 4). FieldDebateView renders "Assigning papers…" from `partial`, and
    // the failed-build message from `partial_error` (no Retry button here —
    // the readOnly route passes no onRetry).
    partial: data.debate_map.partial,
    partial_error: data.debate_map.partial_error,
  };

  return (
    <div className="flex min-h-screen flex-col bg-background text-foreground">
      {/* Header */}
      <div className="border-b px-6 py-3">
        <h1 className="text-lg font-semibold">{data.concept_name}</h1>
        <p className="text-xs text-muted-foreground">Field Debate Map — shared view</p>
      </div>

      {/* Debate view */}
      <div className="flex-1" style={{ height: "calc(100vh - 80px)" }}>
        <FieldDebateView
          debateMap={debateMap as any}
          conceptId=""
          readOnly
        />
      </div>

      {/* Footer */}
      <div className="border-t px-6 py-3 text-center text-xs text-muted-foreground">
        Built with{" "}
        <a href="/" className="font-medium text-foreground hover:underline">
          PaperChase
        </a>{" "}
        — AI-powered literature review
      </div>
    </div>
  );
}
